import { supabase } from '../supabase';

// Carga de la base de volumen de industria (superadmin): se abre una carga, se
// mandan las filas por partes, se carga cada temporada (cada una en su
// transacción) y se cierra.

export type IndustryVolumeCommitResult = {
  mode: 'replace' | 'append';
  seasons: string[];
  deleted_rows: number;
  inserted_rows: number;
  mappings: {
    rows: number;
    clients_linked: number;
    ports_unmatched: number;
    ports_unmatched_kilos: number;
    ships_unmatched: number;
    ships_unmatched_kilos: number;
  };
};

export const startIndustryVolumeUpload = async (
  speciesId: number,
  fileName: string,
  mode: 'replace' | 'append'
): Promise<string> => {
  const { data, error } = await supabase.rpc('start_industry_volume_upload', {
    p_species_id: speciesId,
    p_file_name: fileName,
    p_mode: mode,
  });
  if (error) throw new Error(error.message);
  return data as string;
};

export const stageIndustryVolumeRows = async (
  uploadId: string,
  rows: unknown[]
): Promise<number> => {
  const { data, error } = await supabase.rpc('stage_industry_volume_rows', {
    p_upload_id: uploadId,
    p_rows: rows,
  });
  if (error) throw new Error(error.message);
  return data as number;
};

// Carga una temporada (la reemplaza o la agrega) en su propia transacción.
export const commitIndustryVolumeSeason = async (
  uploadId: string,
  season: string
): Promise<{ season: string; deleted_rows: number; inserted_rows: number }> => {
  const { data, error } = await supabase.rpc('commit_industry_volume_season', {
    p_upload_id: uploadId,
    p_season: season,
  });
  if (error) throw new Error(error.message);
  return data;
};

export const finishIndustryVolumeUpload = async (
  uploadId: string
): Promise<IndustryVolumeCommitResult> => {
  const { data, error } = await supabase.rpc('finish_industry_volume_upload', {
    p_upload_id: uploadId,
  });
  if (error) throw new Error(error.message);
  return data as IndustryVolumeCommitResult;
};

// Equivalencias (superadmin) --------------------------------------------------

export type IndustryClientMatch = {
  exporter_id: number;
  exporter_name: string;
  exporter_key: string | null;
  names: string[] | null;
  kilos: number | null;
  last_season: string | null;
  // linked: confirmado. suggested: el sistema lo propone. none: sin nombres.
  status: 'linked' | 'suggested' | 'none';
};

export type IndustryExporterGroup = {
  exporter_key: string;
  names: string[];
  kilos: number;
  last_season: string;
  exporter_id: number | null;
};

export type IndustryUnmatchedValue = {
  raw: string;
  detail: string | null;
  kilos: number;
  row_count: number;
  last_season: string;
};

// El error lleva el código de Postgres: 42501 = sin acceso.
export type IndustryVolumeError = Error & { code?: string };

const rpc = async <T>(name: string, params: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.rpc(name, params);
  if (error) {
    const failure: IndustryVolumeError = new Error(error.message);
    failure.code = error.code;
    throw failure;
  }
  return data as T;
};

export const getIndustryClientMatches = (speciesId: number) =>
  rpc<IndustryClientMatch[]>('industry_volume_client_matches', {
    p_species_id: speciesId,
  });

export const getIndustryExporterGroups = (speciesId: number) =>
  rpc<IndustryExporterGroup[]>('industry_volume_exporter_groups', {
    p_species_id: speciesId,
  });

export const getIndustryUnmatched = (speciesId: number, kind: 'port' | 'ship') =>
  rpc<IndustryUnmatchedValue[]>('industry_volume_unmatched', {
    p_species_id: speciesId,
    p_kind: kind,
  });

// Exportadora: raw es la clave; exporterId null marca "no es de ningún cliente".
export const setIndustryVolumeAlias = (params: {
  speciesId: number;
  kind: 'exporter' | 'port' | 'ship';
  raw: string;
  exporterId?: number | null;
  marketId?: string | null;
  shipId?: number | null;
  remove?: boolean;
}) =>
  rpc<number>('set_industry_volume_alias', {
    p_species_id: params.speciesId,
    p_kind: params.kind,
    p_raw: params.raw,
    p_exporter_id: params.exporterId ?? null,
    p_market_id: params.marketId ?? null,
    p_ship_id: params.shipId ?? null,
    p_remove: params.remove ?? false,
  });

export const getMarketOptions = async () => {
  const { data, error } = await supabase
    .from('markets')
    .select('id, name, country')
    .order('name');
  if (error) throw new Error(error.message);
  return (data || []) as { id: string; name: string; country: string | null }[];
};

export const getShipOptions = async () => {
  const { data, error } = await supabase.from('ships').select('id, name').order('name');
  if (error) throw new Error(error.message);
  return (data || []) as { id: number; name: string }[];
};

// Pulso de temporada ----------------------------------------------------------

// weeks: semanas de la temporada en orden (getSeasonWeeksForSpecies).
// basis: 'etd' por semana de zarpe, 'eta' por semana de arribo.
export const getIndustryVolumePulse = (
  speciesId: number,
  season: string,
  weeks: number[],
  basis: 'etd' | 'eta' = 'etd',
  exporterId?: number | null
) =>
  // El tipo del resultado vive con sus cálculos (admin: build-pulse.ts).
  rpc<unknown>('get_industry_volume_pulse', {
    p_species_id: speciesId,
    p_season: season,
    p_weeks: weeks,
    p_exporter_id: exporterId ?? null,
    p_basis: basis,
  });

// Naves de la temporada por fecha de arribo o de zarpe (basis) (admin:
// build-arrivals.ts). Variedades y exportadoras (exporter_key) filtran en el
// servidor; vacío: todas.
export const getIndustryVolumeArrivals = (
  speciesId: number,
  season: string,
  filters: { varieties?: string[]; exporterKeys?: string[] } = {},
  basis: 'etd' | 'eta' = 'eta',
  exporterId?: number | null
) =>
  rpc<unknown>('get_industry_volume_arrivals', {
    p_species_id: speciesId,
    p_season: season,
    p_exporter_id: exporterId ?? null,
    p_varieties: filters.varieties?.length ? filters.varieties : null,
    p_exporter_keys: filters.exporterKeys?.length ? filters.exporterKeys : null,
    p_basis: basis,
  });

// Detalle de una barra del gráfico de naves (todas las exportadoras, variedades
// o países, sin "Otros"): [clave, nombre, kilos, kilos tuyos].
export const getIndustryVolumeArrivalsDetail = (
  speciesId: number,
  season: string,
  basis: 'etd' | 'eta',
  range: { from: string; to: string },
  dimension: 'exporter' | 'variety' | 'country',
  filters: {
    varieties?: string[];
    exporterKeys?: string[];
    destinations?: string[];
    vias?: string[];
    period?: 'all' | 'arrived' | 'pending';
  } = {}
) =>
  rpc<[string, string, number, number][]>('get_industry_volume_arrivals_detail', {
    p_species_id: speciesId,
    p_season: season,
    p_basis: basis,
    p_from: range.from,
    p_to: range.to,
    p_dimension: dimension,
    p_varieties: filters.varieties?.length ? filters.varieties : null,
    p_exporter_keys: filters.exporterKeys?.length ? filters.exporterKeys : null,
    p_destinations: filters.destinations?.length ? filters.destinations : null,
    p_vias: filters.vias?.length ? filters.vias : null,
    p_period: filters.period ?? 'all',
  });

// Curva de una o varias variedades en todas las temporadas: [temporada, índice de semana, kilos].
export const getIndustryVolumeCurves = (
  speciesId: number,
  weeks: number[],
  varieties: string[],
  basis: 'etd' | 'eta' = 'etd'
) =>
  rpc<[string, number, number][]>('get_industry_volume_curves', {
    p_species_id: speciesId,
    p_weeks: weeks,
    p_varieties: varieties,
    p_basis: basis,
  });

// Superadmin: vuelve a calcular los arribos (por defecto, la última temporada).
export const refreshIndustryVolumeEta = (speciesId: number, season?: string | null) =>
  rpc<{ season: string; updated_rows: number; by_source: Record<string, number> }>(
    'refresh_industry_volume_eta',
    { p_species_id: speciesId, p_season: season ?? null }
  );
